import { MouseEvent as ReactMouseEvent, ReactElement, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { elementTypeToLabelSingular } from '@domainConstants'
import { DeleteForever, Visibility } from '@mui/icons-material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import { IconButton, Popover, Stack, Typography, useTheme } from '@mui/material'
import { getGuidToMGroup } from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import { ToggleButton } from '@ui/actions'
import { IFC } from '@ui/icons'
import { HeaderBadge } from '@ui/structure'
import { useControlStore, useEditElementStore, useElementSelectionStore } from '@editorStores'
import {
  useResultsSelection,
  useElementLabel,
  useGuidToElementIncludingRips,
  useIfcModelMetadata,
  useIfcSelection,
} from '@editorHooks'
import { getContrastTextColor } from 'src/utils/color'

const VisibilitySelector = (): ReactElement => {
  const theme = useTheme()
  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const addHiddenElementIds = useControlStore(state => state.addHiddenElementIds)
  const removeHiddenElementIds = useControlStore(state => state.removeHiddenElementIds)
  const setAllElementsVisible = useControlStore(state => state.setAllElementsVisible)
  const activeIfcFileGuid = useControlStore(state => state.activeIfcFileGuid)
  const isAnchorCalculationMode = useResultsSelection().activeMode === 'tensile-connectors-table'
  const selectedStandAloneIds = useElementSelectionStore(state => state.selectedStandAloneIds)
  const deselectAllIds = useElementSelectionStore(state => state.deselectAllIds)
  const { selectedIfcElementGuids } = useIfcSelection()
  const activeElement = useEditElementStore(state => state.activeElement)
  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)

  const [hiddenListAnchorEl, setHiddenListAnchorEl] = useState<HTMLElement | null>(null)
  const [hoveredHiddenElementId, setHoveredHiddenElementId] = useState<string | null>(null)

  const getElementLabel = useElementLabel()
  const ifcMetadata = useIfcModelMetadata(activeIfcFileGuid)
  const guidToElement = useGuidToElementIncludingRips()

  const hiddenElementIdsArray = useMemo(() => Array.from(hiddenElementIds), [hiddenElementIds])
  const hiddenListOpen = Boolean(hiddenListAnchorEl)
  const guidToMGroup = useMemo(() => getGuidToMGroup(ifcMetadata), [ifcMetadata])
  const guidToIfcColor = useMemo(() => {
    const ifcPalette = theme.scenePalette.ifc as Record<string, string>
    return Object.entries(guidToMGroup).reduce((acc, [guid, type]) => {
      const color = ifcPalette[type]
      if (color) {
        acc[guid] = color
      }
      return acc
    }, {} as Record<string, string>)
  }, [guidToMGroup, theme.scenePalette.ifc])

  // Hide the current selection in one action. The editor has several selection sources depending on
  // the page (structural-planning / IFC-planar use `activeElement`+`multiActiveElements`, IFC uses
  // `selectedIfcElementGuids`, global loads use `selectedStandAloneIds`), so we union all of them.
  // Hiding is already unified via `hiddenElementIds`, so this doesn't need a merged selection store.
  const selectedIdsToHide = useMemo(
    () =>
      Array.from(
        new Set([
          ...(activeElement ? [activeElement] : []),
          ...multiActiveElements,
          ...selectedStandAloneIds,
          ...selectedIfcElementGuids,
        ]),
      ),
    [activeElement, multiActiveElements, selectedStandAloneIds, selectedIfcElementGuids],
  )

  const hideSelection = () => {
    if (selectedIdsToHide.length === 0) return

    addHiddenElementIds(selectedIdsToHide)
    // Hiding is a view-only action: it must not deselect. `activeElement`/`multiActiveElements`/
    // `selectedIfcElementGuids` are the real selection (backed by the global selection store), so
    // clearing them here would deselect the element being hidden. `deselectAllIds` is unrelated
    // (the transient pick-flow store), so it still runs.
    deselectAllIds()
  }

  useHotkeys(['v'], hideSelection, { enabled: !isAnchorCalculationMode }, [
    selectedIdsToHide,
    isAnchorCalculationMode,
  ])

  useHotkeys(
    ['shift+v'],
    () => {
      setAllElementsVisible()
    },
    { enabled: !isAnchorCalculationMode },
    [isAnchorCalculationMode],
  )

  return (
    <Stack direction="row">
      <HeaderBadge
        badgeContent={hiddenElementIds.size}
        color="warning"
        data-cy="badge-hidden-elements"
      >
        <ToggleButton
          hint="Auswahl ausblenden"
          onClick={hideSelection}
          selected={false}
          disabled={selectedIdsToHide.length === 0}
          Icon={Visibility}
          data-cy="btn-hide"
        />
      </HeaderBadge>
      <ToggleButton
        hint="Ausgeblendete Elemente anzeigen"
        onClick={(event: ReactMouseEvent<HTMLElement>) => {
          setHiddenListAnchorEl(current => (current ? null : event.currentTarget))
        }}
        selected={hiddenListOpen}
        Icon={FormatListBulletedIcon}
        data-cy="btn-hidden-elements-list"
      />
      <ToggleButton
        hint="Elemente einblenden"
        onClick={() => {
          setAllElementsVisible()
          setHoveredHiddenElementId(null)
          setHiddenListAnchorEl(null)
        }}
        selected={false}
        Icon={DeleteForever}
        data-cy="btn-hide-reset"
      />

      <Popover
        open={hiddenListOpen}
        anchorEl={hiddenListAnchorEl}
        onClose={() => {
          setHiddenListAnchorEl(null)
          setHoveredHiddenElementId(null)
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: { mt: 1, p: 1.5, minWidth: 360 },
          },
        }}
      >
        <Stack spacing={0.5}>
          <Typography align="center">Ausgeblendete Elemente</Typography>
          {hiddenElementIdsArray.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Keine ausgeblendeten Elemente
            </Typography>
          ) : (
            hiddenElementIdsArray.map(hiddenElementId => (
              <Stack
                key={hiddenElementId}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  {guidToIfcColor[hiddenElementId] && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <IFC />
                      <Typography fontSize="small">IFC Element</Typography>
                    </Stack>
                  )}
                  {(() => {
                    const label = getElementLabel(hiddenElementId)
                    const ifcColor = guidToIfcColor[hiddenElementId]
                    const hasLabel = label !== hiddenElementId
                    const elementType = guidToElement[hiddenElementId]?.element_type as
                      | ElementTypes
                      | undefined
                    const elementTypeLabel = elementType
                      ? elementTypeToLabelSingular[elementType] || elementType
                      : null
                    const text = ifcColor
                      ? hasLabel
                        ? `${label} (${hiddenElementId})`
                        : hiddenElementId
                      : elementTypeLabel
                      ? hasLabel
                        ? `${elementTypeLabel} ${label} (${hiddenElementId})`
                        : `${elementTypeLabel} ${hiddenElementId}`
                      : hasLabel
                      ? `${label} (${hiddenElementId})`
                      : `${hiddenElementId}`

                    if (!ifcColor) {
                      return (
                        <Typography component="span" fontSize="inherit">
                          {text}
                        </Typography>
                      )
                    }

                    return (
                      <Typography
                        component="span"
                        fontSize="inherit"
                        sx={{
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.75,
                          bgcolor: ifcColor,
                          color: getContrastTextColor(ifcColor),
                        }}
                      >
                        {text}
                      </Typography>
                    )
                  })()}
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      removeHiddenElementIds([hiddenElementId])
                      if (hoveredHiddenElementId === hiddenElementId) {
                        setHoveredHiddenElementId(null)
                      }
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            ))
          )}
        </Stack>
      </Popover>
    </Stack>
  )
}

export default VisibilitySelector
